import { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext'
import { ToastProvider } from './context/ToastContext'
import { ErrorBoundary } from './components/ErrorBoundary'
import { OfflineIndicator } from './components/OfflineIndicator'
import { ProtectedRoute } from './components/ProtectedRoute'
import { SkipLink } from './components/SkipLink'

// Lazy load all page components for code splitting (bundle-dynamic-imports)
const AuthCallback = lazy(() => import('./pages/AuthCallback'))
const EmailVerification = lazy(() => import('./pages/EmailVerification'))
const VerifyToken = lazy(() => import('./pages/VerifyToken'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const ResumeUpload = lazy(() => import('./pages/ResumeUpload'))
const ResumeList = lazy(() => import('./pages/ResumeList'))
const ResumeDetail = lazy(() => import('./pages/ResumeDetail'))
const JobSubmission = lazy(() => import('./pages/JobSubmission'))
const JobList = lazy(() => import('./pages/JobList'))
const JobDetail = lazy(() => import('./pages/JobDetail'))
const TailoringWizard = lazy(() => import('./pages/TailoringWizard'))
const TailoredResumeDetail = lazy(() => import('./pages/TailoredResumeDetail'))
const TailoredResumesList = lazy(() => import('./pages/TailoredResumesList'))
const Pricing = lazy(() => import('./pages/Pricing'))
const BillingSettings = lazy(() => import('./pages/BillingSettings'))
const LandingPage = lazy(() => import('./pages/LandingPage'))
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })))
const AccountDeletion = lazy(() => import('./pages/AccountDeletion'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const TermsOfService = lazy(() => import('./pages/TermsOfService'))
const Login = lazy(() => import('./pages/Login'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))

// Minimal loading fallback for route transitions
function PageLoader() {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="relative w-10 h-10">
        <div className="absolute inset-0 rounded-full border-2 border-slate-700" />
        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-blue-500 animate-spin" />
      </div>
    </div>
  )
}


function App() {
  return (
    <ErrorBoundary>
      <Router>
        <ToastProvider>
          <AuthProvider>
            <SkipLink />
            <OfflineIndicator />
            <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/verify" element={<VerifyToken />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />

              {/* Email verification route (protected but doesn't require verified email) */}
              <Route
            path="/verify-email"
            element={
              <ProtectedRoute requireEmailVerification={false}>
                <EmailVerification />
              </ProtectedRoute>
            }
          />

          {/* Protected routes (require authentication and verified email) */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resumes/upload"
            element={
              <ProtectedRoute>
                <ResumeUpload />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resumes"
            element={
              <ProtectedRoute>
                <ResumeList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resumes/:id"
            element={
              <ProtectedRoute>
                <ResumeDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/new"
            element={
              <ProtectedRoute>
                <JobSubmission />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs"
            element={
              <ProtectedRoute>
                <JobList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/:id"
            element={
              <ProtectedRoute>
                <JobDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tailor"
            element={
              <ProtectedRoute>
                <TailoringWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tailored-resumes"
            element={
              <ProtectedRoute>
                <TailoredResumesList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tailored-resumes/:id"
            element={
              <ProtectedRoute>
                <TailoredResumeDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/billing"
            element={
              <ProtectedRoute>
                <BillingSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/delete"
            element={
              <ProtectedRoute>
                <AccountDeletion />
              </ProtectedRoute>
            }
          />

          {/* Catch-all 404 route - must be last */}
          <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
          </AuthProvider>
        </ToastProvider>
      </Router>
    </ErrorBoundary>
  )
}

export default App
